<script setup lang="ts">
import { modalController } from '@ionic/vue';
import { computed, inject, defineAsyncComponent, shallowRef } from 'vue';
import { useStore } from '@/stores/main';
import type { ProfileValueChange } from '@/types';
import Info from '@/components/icons/Info.vue';

// Lazy load ImageViewer for better performance
const ImageViewer = defineAsyncComponent(() => import('@/components/ImageViewer.vue'));

const props = defineProps({
  data: {
    type: Object,
    required: true,
    validator: (value: any) => {
      return value && typeof value.name === 'string';
    }
  },
});

const emit = defineEmits<{
  (e: 'change', value: ProfileValueChange): void;
  (e: 'update'): void;
}>();

const store = useStore();
const questionData = inject('questionData');

// Modal state management
const isModalOpen = shallowRef(false);

// Optimized computed property with caching
const modelProxy = computed({
  get: () => store.profile.values[props.data.name],
  set: (val) => {
    // Prevent unnecessary emissions
    if (val !== store.profile.values[props.data.name]) {
      emit('change', { name: props.data.name, value: val });
    }
  }
});

// Memoized computed properties
const hasPoster = computed(() => props.data.poster?.url != null);
const posterText = computed(() => props.data.poster?.text || '');
const dataSuffix = computed(() => props.data.data?.suffix || '');

// Optimized modal handler
const showPoster = async (posterUrl: string) => {
  if (isModalOpen.value) return;
  
  isModalOpen.value = true;
  
  try {
    const modal = await modalController.create({
      component: ImageViewer,
      componentProps: {
        poster: posterUrl,
      },
      cssClass: 'image-viewer-modal',
    });
    
    await modal.present();
    await modal.onWillDismiss();
  } catch (error) {
    console.error('Error showing poster:', error);
  } finally {
    isModalOpen.value = false;
  }
};

// Click handler for poster link
const handlePosterClick = (event: Event) => {
  event.preventDefault();
  if (hasPoster.value) {
    showPoster(props.data.poster.url);
  }
};
</script>

<template>
  <div 
    :data-name="data.name" 
    class="lg:w-[60%] text-left textbox-container"
  >
    <p 
      class="text-base mt-0 description"
      v-html="data.description"
    />
    
    <div 
      v-if="hasPoster" 
      class="mb-4 poster-container"
    >
      <a
        @click="handlePosterClick"
        href="#"
        class="flex items-center gap-x-1 no-underline text-black hover:text-green-darker transition-colors duration-200"
        :aria-label="`View ${posterText}`"
      >
        <Info 
          class="text-green-darker flex-shrink-0" 
          width="20" 
          aria-hidden="true"
        />
        <span class="text-base leading-none relative top-[1px]">
          Tryck här för att se {{ posterText }}
        </span>
      </a>
    </div>
    
    <div class="flex mb-6 items-center input-container">
      <b-input 
        :id="data.name" 
        v-model="modelProxy"
        :aria-label="data.description"
        class="flex-1"
      />
      <span
        v-if="dataSuffix"
        class="ml-2 w-8 suffix"
        v-text="dataSuffix"
        aria-label="suffix"
      />
    </div>
  </div>
</template>

<style scoped>
/* Container optimizations */
.textbox-container {
  contain: layout style;
}

/* Description styling */
.description {
  white-space: pre-wrap;
  word-break: break-word;
}

/* Poster container */
.poster-container a {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

/* Input container */
.input-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Suffix styling */
.suffix {
  flex-shrink: 0;
  user-select: none;
}

/* Accessibility improvements */
a:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

/* Performance optimizations */
.textbox-container,
.description,
.poster-container,
.input-container {
  will-change: auto;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .textbox-container {
    width: 100%;
  }
  
  .input-container {
    flex-wrap: wrap;
  }
}
</style>